import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { useNavigate } from '@/components/Link/Link';
import {
  PageLayout,
  PageLayoutContent,
  PageLayoutLeftPart,
  PageLayoutLeftPartContent,
  PageLayoutSimpleHeader,
} from '@/components/PageLayout';
import AlertBar from '@/design_system/AlertBar';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import FoldableSection from '@/design_system/FoldableSection';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer/Footer';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import Title from '@/design_system/Typography/Title';
import IconArrow from '@/icons/Arrow.svg';
import IconClock from '@/icons/Clock.svg';
import IconInfo from '@/icons/Info.svg';
import { isInDraftStep, useSendDraftRequest } from '@/models/request';
import { useDatabaseTranslation } from '@/services/i18n';
import { useScrollIntoView } from '@/utils/useScrollIntoView';
import useViewPort from '@/utils/useViewport';

import { ArchiveRequestButton } from '../../components/ArchiveRequestButton';
import { useRequestContext } from '../../contexts/RequestContext';
import { RequestComments } from '../../Request/components/RequestComments';

import Articles from './Articles';
import ClientStoreForm, { getClientStoreError } from './ClientStoreForm';
import { creationErrorsFilter, saveForLaterErrorsFilter } from './NewArticleFooter';
import { PrintDropOffReceiptButton, PrintDropOffReceiptModal } from './PrintDropOffReceipt';
import RequestInformationForm from './RequestInformationForm';

export const RequestForm = ({ onNext }: { onNext?: () => void }) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { isMobile } = useViewPort();
  const { request, workflow, articles, view, state, hasError } = useRequestContext();

  const [showPrintDropOffModal, setShowPrintDropOffModal] = useState(false);

  const goToRequests = ({ skipRouterBlocker }: { skipRouterBlocker: boolean }) =>
    navigate('/requests', { state: { skipRouterBlocker }, replace: true });
  const goToRequest = ({ skipRouterBlocker }: { skipRouterBlocker: boolean }) =>
    navigate(`/requests/${request.id}`, { state: { skipRouterBlocker }, replace: true });

  // Errors
  const hasClientStoreError = getClientStoreError(request, view.client);
  const [showClientStoreError, setShowClientStoreError] = useState(false);

  const [alertRef, scrollAlertIntoView] = useScrollIntoView<HTMLDivElement>();
  const [apiError, setApiError] = useState('');

  // Actions

  const handleApiError = (err: any) => {
    console.error(err);
    setApiError(
      (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
    );
  };

  const {
    mutateAsync: saveForLater,
    isPending: isPendingSaveForLater,
    isSuccess: isSuccessSaveForLater,
  } = useSendDraftRequest(request.id);

  const {
    mutateAsync: create,
    isPending: isPendingCreate,
    isSuccess: isSuccessCreate,
  } = useSendDraftRequest(request.id);

  const handleSaveForLater = () => {
    const { hasError } = state.errors.check({
      articles: {
        articles: articles.map((article) => ({
          ...saveForLaterErrorsFilter(),
          id: article.articleId,
        })),
        noArticles: true,
      },
    });

    setShowClientStoreError(true);

    if (hasClientStoreError || hasError) {
      scrollAlertIntoView();

      return;
    }

    const isDraft = request.draft;

    saveForLater({ saveForLater: true })
      .then(() => {
        if (
          request.client &&
          request.store &&
          isDraft &&
          workflow?.config.dropOffReceipt?.enabled
        ) {
          setShowPrintDropOffModal(true);
        } else {
          goToRequests({ skipRouterBlocker: true });
        }

        state.errors.check(null);
      })
      .catch(handleApiError);
  };

  const initialCustomFieldErrors = view.information.customFields.map((customField) => ({
    id: customField.id,
    missingValue: true,
  }));

  const handleSaveRequest = () => {
    const { hasError } = state.errors.check({
      information: {
        missingSalesAdvisor:
          view.information.salesAdvisor.enabled && view.information.salesAdvisor.required,
        missingCustomFieldValues: initialCustomFieldErrors,
      },
      articles: {
        articles: articles.map((article) => ({
          ...creationErrorsFilter(article.view, view),
          id: article.articleId,
        })),
        noArticles: true,
      },
      price: {
        missingWaiveHandlingFeeReason: view.organizationPrice.shown,
        missingWaiveHandlingFeeOtherReason: view.organizationPrice.shown,
        missingDepositChoice: view.organizationPrice.shown,
      },
    });

    setShowClientStoreError(true);

    if (hasClientStoreError || hasError) {
      scrollAlertIntoView();

      return;
    }

    if (onNext) {
      onNext();

      return;
    }

    create({ saveForLater: false })
      .then(() => {
        goToRequest({ skipRouterBlocker: true });
        state.errors.check(null);
      })
      .catch(handleApiError);
  };

  const saveForLaterLabel = t({
    id: 'requests.edit.actions.save-for-later',
    message: 'Save for later',
  });

  // Only draft requests here
  if (!request.draft && !isInDraftStep(request)) {
    goToRequest({ skipRouterBlocker: true });

    return null;
  }

  return (
    <PageLayout>
      <PageLayoutSimpleHeader
        title={
          request.draft
            ? t({ id: 'requests.new.title', message: 'New request' })
            : t({ id: 'requests.edit.title', message: 'Edit request' })
        }
        subtitle={
          onNext ? t({ id: 'requests.new.subtitle', message: 'Step 1/2: Information' }) : undefined
        }
        onClose={() => goToRequests({ skipRouterBlocker: !request.draft })}
      />

      <PageLayoutContent>
        <PageLayoutLeftPart>
          <PageLayoutLeftPartContent>
            <Stack padding={isMobile ? '1rem' : undefined} gap="1rem" ref={alertRef}>
              <NewRequestInfoBanners />

              {(hasError || (showClientStoreError && hasClientStoreError)) && (
                <AlertBar
                  type="error"
                  title={
                    request.draft
                      ? t({
                          id: 'requests.new.error',
                          message:
                            'Please fill the missing information in order to be able to create a request',
                        })
                      : t({
                          id: 'requests.edit.error',
                          message:
                            'Please fill the missing information in order to be able to edit the request',
                        })
                  }
                />
              )}

              <Stack gap="2rem">
                <RequestInformationForm />
                <ClientStoreForm showErrors={showClientStoreError && hasClientStoreError} />
                <Articles />
                <FoldableSection
                  label={
                    <Title as="h2">
                      <Trans id="comments.title">Comments</Trans>
                    </Title>
                  }
                  labelOutsideButton={
                    <Tooltip
                      content={t({
                        id: 'comments.title.tooltip',
                        message: "These comments won't be visible by the client",
                      })}
                    >
                      <Button variant="style-less">
                        <IconInfo className="text-disabled" style={{ fontSize: '1.25rem' }} />
                      </Button>
                    </Tooltip>
                  }
                >
                  <Box padding="0" removeStyleOnMobile>
                    <RequestComments requestId={request.id} />
                  </Box>
                </FoldableSection>
              </Stack>
            </Stack>
          </PageLayoutLeftPartContent>

          <Footer>
            {apiError && <FooterError>{apiError}</FooterError>}
            <FooterButtons>
              {!onNext && (
                <>
                  {request.draft ? (
                    <>
                      <Button
                        variant="secondary"
                        size="medium"
                        onPress={handleSaveForLater}
                        isLoading={isPendingSaveForLater || isSuccessSaveForLater}
                        dataTrackingId="requests.new.actions.save-for-later"
                      >
                        <IconClock />
                        {saveForLaterLabel}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Stack row gap={isMobile ? '0.5rem' : '1rem'}>
                        <ArchiveRequestButton
                          request={request}
                          onArchive={() => goToRequests({ skipRouterBlocker: true })}
                          onArchiveError={(reason) => {
                            setApiError(
                              ((reason as any).body?.message as string) ??
                                t({ id: '_general.error.unknown', message: 'Unknown error' })
                            );
                          }}
                        />
                        {request.client &&
                          request.store &&
                          workflow?.config.dropOffReceipt?.enabled && (
                            <PrintDropOffReceiptButton request={request} />
                          )}
                        <Button
                          variant="secondary"
                          size="medium"
                          onPress={handleSaveForLater}
                          isLoading={isPendingSaveForLater || isSuccessSaveForLater}
                          style={isMobile ? { flex: 1 } : undefined}
                          ariaLabel={isMobile ? saveForLaterLabel : undefined}
                          dataTrackingId="requests.new.actions.save-for-later"
                        >
                          <IconClock />
                          {!isMobile && saveForLaterLabel}
                        </Button>
                      </Stack>
                    </>
                  )}
                  <Button
                    variant="primary"
                    size="medium"
                    onPress={handleSaveRequest}
                    isLoading={isPendingCreate || isSuccessCreate}
                    dataTrackingId="requests.new.actions.create"
                  >
                    {request.draft ? (
                      <Trans id="requests.new.actions.create">Create request</Trans>
                    ) : (
                      <Trans id="requests.edit.actions.save-and-create">Save and create</Trans>
                    )}
                  </Button>
                </>
              )}
              {onNext && (
                <Button variant="primary" size="medium" onPress={handleSaveRequest}>
                  <Trans id="requests.new.actions.next">Next</Trans>
                  <IconArrow right />
                </Button>
              )}
            </FooterButtons>
          </Footer>
        </PageLayoutLeftPart>
      </PageLayoutContent>
      {showPrintDropOffModal && (
        <PrintDropOffReceiptModal
          request={request}
          onClose={() => goToRequests({ skipRouterBlocker: true })}
        />
      )}
    </PageLayout>
  );
};

const NewRequestInfoBanners = () => {
  const { workflow } = useRequestContext();
  const { _db } = useDatabaseTranslation();

  if (!workflow?.config.newRequestInfoBanners?.length) {
    return null;
  }

  return (
    <>
      {workflow.config.newRequestInfoBanners.map((newRequestInfoBanner, key) => (
        <AlertBar
          key={key}
          type={newRequestInfoBanner.type}
          title={_db(newRequestInfoBanner.message)}
        />
      ))}
    </>
  );
};
